import React, { useCallback, useContext } from 'react';
import { Link } from 'react-router-dom';

import { AppContext } from '@prisjakt/ui-app-context';
import ProductImage from '@prisjakt/ui-product-image';
import Currency from '@prisjakt/ui-currency';

import Placeholder from '../../../placeholder';
import { Direction } from '../../../placeholder/types';
import SentimentBadge from '../../../../../../components/sentiment-badge';
import ViewportContext from '../../../../../../components/viewport/context';
import { trackComparisonSelectionChanged } from '../../../../tracking';
import {
  imageWidthVal,
  imageWidthMobileVal,
  desktopMaxColumns,
  features,
} from '../../../../constants';

import { Product } from '../../../../types';
import {
  CategoryPropsFragment,
  ProductPriceSummary,
} from '../../../../../../types';

import * as S from './styles';
import { IconClose } from '@prisjakt/ui/icon';

type OwnProps = {
  category?: CategoryPropsFragment['category'];
  isDummyHeader?: boolean;
  product?: Product;
  products?: Product[];
  shouldAnimate?: boolean;
  hasMaxWidth?: boolean;
};

const Header = ({
  category,
  isDummyHeader,
  product,
  products,
  shouldAnimate,
  hasMaxWidth,
}: OwnProps) => {
  const { linkTo, t } = useContext(AppContext);
  const { isDesktop, isMobile } = useContext(ViewportContext);

  const trackRemovedProduct = useCallback(() => {
    trackComparisonSelectionChanged(
      products!.length - 1,
      'remove',
      'productCard'
    );
  }, [products]);

  const hideSentimentBadge = () => {
    return Boolean(
      !products!.filter(item => item.reviewBadges && item.reviewBadges.length)
        .length
    );
  };

  const hideRatings = () => {
    return Boolean(
      !products!.filter(
        item => item.userReviewSummary && item.userReviewSummary.rating
      ).length
    );
  };

  const renderImage = () =>
    product!.media && product!.media.first ? (
      <ProductImage src={product!.media.first} name={product!.name} />
    ) : null;

  const renderPrice = (priceSummary: ProductPriceSummary) =>
    priceSummary && priceSummary.regular ? (
      <Currency price={priceSummary.regular} />
    ) : null;

  const renderRating = () => (
    <S.RatingWrapper hide={hideRatings()}>
      {product!.userReviewSummary && product!.userReviewSummary.rating ? (
        <S.Rating
          rate={product!.userReviewSummary.rating}
          count={product!.userReviewSummary.count}
          showRate
          showCounterAs="number"
          showCounter={isDesktop}
        />
      ) : null}
    </S.RatingWrapper>
  );

  const renderShippingPrice = (priceSummary: ProductPriceSummary) => {
    let shippingValue: string | React.ReactNode;
    if (!priceSummary.includeShipping) {
      shippingValue = t('product', 'price.unknown-shipping');
    } else {
      shippingValue =
        priceSummary.includeShipping === priceSummary.regular
          ? t('product', 'price.free-shipping')
          : t('product', 'price.including-shipping', {
              price: (
                <Currency key="currency" price={priceSummary.includeShipping} />
              ),
            });
    }

    return shippingValue;
  };

  const renderSentimentBadge = () => (
    <S.SentimentBadge hide={hideSentimentBadge()}>
      {product!.reviewBadges &&
        product!.reviewBadges
          .slice(0, 1)
          .map((badge: any) => (
            <SentimentBadge
              badge={badge}
              key={badge.id}
              showNrOfReviewsAs={isDesktop ? 'number' : undefined}
            />
          ))}
    </S.SentimentBadge>
  );

  const renderDummyHeader = () => {
    const placeholderItems = [
      {
        id: 1,
        width: isMobile ? imageWidthMobileVal : imageWidthVal,
        height: isMobile ? imageWidthMobileVal : imageWidthVal,
      },
      {
        id: 2,
        width: 190,
        height: 30,
      },
      {
        id: 3,
        width: 90,
        height: 20,
      },
      {
        id: 4,
        width: 190,
        height: 20,
      },
    ];

    return (
      <S.DummyHeader
        key="header-dummy"
        hasMaxWidth={!products || products.length === 0}
      >
        <Placeholder direction={Direction.COLUMN} items={placeholderItems} />
      </S.DummyHeader>
    );
  };

  if (isDummyHeader) return renderDummyHeader();

  if (!products || !product) return null;

  return (
    <S.Header
      key={`header-${product.id}`}
      desktopScaleToFit={products && products.length >= desktopMaxColumns}
      shouldAnimate={shouldAnimate}
      hasScrollSnap={features.useCSSScrollSnap}
    >
      <S.Image to={linkTo('product', product)}>{renderImage()}</S.Image>
      <S.Title>
        <Link to={linkTo('product', product)}>{product.name}</Link>
      </S.Title>
      {renderRating()}
      {renderSentimentBadge()}
      <S.Price>{renderPrice(product.priceSummary)}</S.Price>
      <S.ShippingPrice>
        {renderShippingPrice(product.priceSummary)}
      </S.ShippingPrice>
      <S.CloseButton
        rel="nofollow"
        to={linkTo('productCompare', {
          category,
          products: products.filter(({ id }) => id !== product.id),
        })}
        onClick={trackRemovedProduct}
      >
        <IconClose color="base400" />
      </S.CloseButton>
    </S.Header>
  );
};

export default Header;
